import { IPermission } from "../../../../compiler/global.interface";
import { PermissionAction } from "./User/permissionInheritance";

export interface PermissionTarget {
  roles_code: string;
  originalIndex: number;
}
export interface PermissionChange {
  index: number;
  action: PermissionAction;
  value: 0 | 1;
}
export const isExplicitDashboardPermission = (roleCode: string): boolean => roleCode === "00-01";
const actions: PermissionAction[] = ["read", "create", "update", "delete", "approve"];
const writeActions = actions.filter((action) => action !== "read");

function targetChanges(
  target: PermissionTarget,
  action: PermissionAction,
  checked: boolean
): PermissionChange[] {
  const index = target.originalIndex;
  const value = checked ? 1 : 0;
  if (isExplicitDashboardPermission(target.roles_code)) {
    // This capability only supports a direct Read edit. Even a synthetic write
    // event must not auto-grant Read; clear unsupported flags without touching it.
    const changes: PermissionChange[] = writeActions.map((action) => ({ index, action, value: 0 }));
    return action === "read" ? [{ index, action, value }, ...changes] : changes;
  }
  if (action === "read" && !checked) return actions.map((action) => ({ index, action, value: 0 }));
  return action !== "read" && checked
    ? [
        { index, action, value },
        { index, action: "read", value: 1 },
      ]
    : [{ index, action, value }];
}

function bulkTargetChanges(
  targets: PermissionTarget[],
  action: PermissionAction,
  checked: boolean
): PermissionChange[] {
  return targets
    .filter((target) => !isExplicitDashboardPermission(target.roles_code))
    .flatMap((target) => targetChanges(target, action, checked));
}

export function permissionRowChanges(
  roleCode: string,
  index: number,
  children: PermissionTarget[],
  action: PermissionAction,
  checked: boolean
): PermissionChange[] {
  return [
    ...targetChanges({ roles_code: roleCode, originalIndex: index }, action, checked),
    ...bulkTargetChanges(children, action, checked),
  ];
}

export function bulkPermissionChanges(
  permissions: IPermission[],
  action: PermissionAction,
  checked: boolean
): PermissionChange[] {
  return bulkTargetChanges(
    permissions.map((permission, originalIndex) => ({
      roles_code: permission.roles_code,
      originalIndex,
    })),
    action,
    checked
  );
}

export function isBulkPermissionChecked(
  permissions: IPermission[],
  action: PermissionAction
): boolean {
  const eligible = permissions.filter(
    (permission) => !isExplicitDashboardPermission(permission.roles_code)
  );
  return eligible.length > 0 && eligible.every((permission) => Number(permission[action]) === 1);
}
